import type { ContractParty } from './parties/types'

export type FieldVisibilityDto = {
  canView: boolean
  canEdit: boolean
}

/** Następny etap dla umowy (z StageSequencer); null gdy etap aktualny jest terminalny. */
export type NextStageRef = {
  code: string
  labelPl: string
}

export type Contract = {
  id: number
  code: string
  configurationCode: string
  assigneeUserId: number | null
  ownerTeamId: number | null
  currentStageCode: string
  totalAmount: number | string
  currency: string
  hasCredit: boolean
  creditStatus: string | null
  signedAt: string | null
  plannedInstallAt: string | null
  completedAt: string | null
  extras: Record<string, unknown>
  createdAt: string
  updatedAt: string
  version: number
  sectionVisibility: Record<string, FieldVisibilityDto>
  parties: ContractParty[]
  /** Nazwa zamawiającego (aktora w roli 'zamawiajacy'). */
  ordererName: string | null
  salesPersonId: number | null
  /** Następny etap w workflow; null gdy etap aktualny jest terminalny. */
  nextStage: NextStageRef | null
}

export type ContractListItem = Contract

export type ContractListResponse = {
  items: ContractListItem[]
}

/**
 * Pełna lista typów pól zgodna z backendem (V20260534_02 + V20260539_01).
 * Backend CHECK constraint na {@code field_definition.field_type} wymusza tę listę --
 * trzymamy ją tutaj jako single source of truth, admin/forms/types.ts re-exportuje.
 */
export type FieldType =
  | 'TEXT'
  | 'NUMBER'
  | 'DATE'
  | 'BOOLEAN'
  | 'SELECT'
  | 'REFERENCE'
  | 'TEXTAREA'
  | 'RICH_TEXT'
  | 'EMAIL'
  | 'PHONE'
  | 'URL'
  | 'IBAN'
  | 'NIP'
  | 'PESEL'
  | 'MULTI_SELECT'
  | 'CHECKBOX_GROUP'
  | 'CURRENCY'
  | 'SEPARATOR'
  | 'SPACER'
  | 'INSTRUCTION'
  | 'HEADER'
  | 'FILE'
  | 'GALLERY'

export type FieldOption = {
  value: string
  labelPl: string
}

export type FieldValidationRules = {
  min?: number | null
  max?: number | null
  regex?: string | null
  maxLength?: number | null
}

export type FieldDefinition = {
  id: number
  objectType: string
  fieldCode: string
  labelPl: string
  fieldType: FieldType
  isRequired: boolean
  isFilterable: boolean
  validation?: FieldValidationRules | null
  options: FieldOption[]
  referenceTable: string | null
  /** Treść tooltipa "?" obok labela (lub tekst dla pola INSTRUCTION). */
  helpText?: string | null
  /** Wymagane dla pola FILE - wskazuje typ dokumentu (z files.document_type). */
  documentTypeId?: number | null
  /** Rola synchronizacyjna - value pola kopiuje się do core column Contract przy zapisie. */
  semanticRole?: string | null
  /** F-VER: tryby wymagane do zatwierdzenia pola (puste = pole nie podlega weryfikacji). */
  verificationModes: VerificationMode[]
  /** F-VER: kody ról uprawnionych do zatwierdzenia. Mirror constraintu chk_field_definition_verify_consistency. */
  verifierRoleCodes: string[]
  /** F-3a: mapowanie source-code -> dot-path w payload (pole bez auto-mapowania = puste). */
  externalKeys: Record<string, string>
  /** Pole built-in (core column aggregate) vs custom (extras JSONB). */
  isBuiltIn: boolean
  isActive: boolean
}

/** Lokalizacja pola w sekcji (N:M - jedno pole moze wystapic w wielu sekcjach). */
export type FieldPlacement = {
  fieldId: number
  sectionId: number
  sortOrder: number
  /** Szerokosc w siatce 12-kolumnowej (1..12). */
  colWidth: number
}

export type VerificationMode = 'CLICK' | 'DOCUMENT' | 'COMMENT'

/** Etykiety PL trybów weryfikacji - używane wszędzie tam gdzie tryb jest pokazywany użytkownikowi. */
export const VERIFICATION_MODE_LABELS: Record<VerificationMode, string> = {
  CLICK: 'Potwierdzenie',
  DOCUMENT: 'Dokument',
  COMMENT: 'Komentarz',
}

export type FormSection = {
  id: number
  objectType: string
  code: string
  labelPl: string
  sortOrder: number
  isActive: boolean
  /** Gdzie sekcja jest renderowana w widoku umowy. */
  renderTarget: 'TAB' | 'SIDEBAR'
}

export type FormSchema = {
  objectType: string
  sections: FormSection[]
  fields: FieldDefinition[]
  /** Polozenie kazdego pola w sekcji (N:M - pole moze byc w wielu sekcjach). */
  placements: FieldPlacement[]
}

export type CreateContractRequest = {
  ordererCustomerId: number
  configurationCode: string
  assigneeUserId?: number | null
  ownerTeamId?: number | null
  totalAmount?: number | null
  currency: string
  hasCredit: boolean
  extras?: Record<string, unknown>
  salesPersonId?: number | null
}

export type UpdateSectionRequest = {
  extras: Record<string, unknown>
}

/** Body dla POST /api/contracts/{id}/override-stage (admin). */
export type OverrideStageRequest = {
  targetStageCode: string
  reason: string
}

export type CancelContractRequest = {
  reason: string
}
